'use client';

import { Button } from '@shared/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Input } from '@shared/components/ui/input';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { cn } from '@shared/utils/utils';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { RECEPTION_STATUS_OPTIONS, ReceptionFilters, ReceptionStatus } from '../../models/Reception';

interface ReceptionsFiltersPanelProps {
  filters: ReceptionFilters;
  onFiltersChange: (filters: ReceptionFilters) => void;
  className?: string;
}

/**
 * Panel de filtros para recepciones (patrón Orders).
 * Layout en grid de 2 columnas: estado, transportista, rango de fechas.
 */
export const ReceptionsFiltersPanel = ({ filters, onFiltersChange, className }: ReceptionsFiltersPanelProps) => {
  const { t } = useTranslation('receptionsModule');

  const handleStatusChange = (value: string) => {
    const newStatus = value === 'all' ? undefined : (value as ReceptionStatus);
    onFiltersChange({ ...filters, status: newStatus });
  };

  const handleCarrierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, carrier: e.target.value || undefined });
  };

  const handleDateRangeChange = (values: { range: DateRange }) => {
    const { from, to } = values.range ?? {};
    onFiltersChange({
      ...filters,
      date_from: from ? format(from, 'yyyy-MM-dd') : undefined,
      date_to: to ? format(to, 'yyyy-MM-dd') : undefined,
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({});
  };

  const activeFiltersCount = [filters.status, filters.date_from || filters.date_to, filters.carrier].filter(
    Boolean,
  ).length;

  return (
    <div className={cn('space-y-5', className)}>
      {/* Campos de filtro (el título "Filtros" ya lo muestra el dropdown/modal) */}
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filter.status')}>
            <Select value={filters.status || 'all'} onValueChange={handleStatusChange}>
              <SelectTrigger className="h-[38px] text-sm bg-background border-input" data-testid="filter-status">
                <SelectValue placeholder={t('filter.all_statuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="text-muted-foreground">{t('filter.all_statuses')}</span>
                </SelectItem>
                {RECEPTION_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField label={t('filter.carrier')}>
            <Input
              placeholder={t('table.carrier')}
              value={filters.carrier || ''}
              onChange={handleCarrierChange}
              className="h-[38px] text-sm bg-background border-input"
              data-testid="filter-carrier"
            />
          </FilterField>

          <FilterField label={t('filter.date_range')} className="sm:col-span-2">
            <DateRangePicker
              initialDateFrom={filters.date_from ? new Date(filters.date_from) : undefined}
              initialDateTo={filters.date_to ? new Date(filters.date_to) : undefined}
              onUpdate={handleDateRangeChange}
              placeholder={t('filter.select_date_range')}
              showPresets={true}
              align="start"
              dataTestId="filter-date-range"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </div>

      {/* Footer con acciones */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="clear-filters-panel"
          >
            {t('filter.clear_filters')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

const FilterField = ({ label, children, className }: FilterFieldProps) => (
  <div className={cn('space-y-1.5', className)}>
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
